import { useItemSet } from '@exam-management/common/item-set-provider';
import {
  OrderType,
  QBankStructureLevelCategory,
  QuestionGroupType,
  useSearchItemSetQuestionsInLocationQuery,
} from '@exam-management/graphql';
import { useQBankStructure } from '@exam-management/qbs-provider';
import { Flex, Text, PaperSection } from '@shared/components';
import { useMemo, useState } from 'react';

import { getUniqueQuestions } from '../../utils';

import ItemSetQuestionsInLocationList from './item-set-questions-in-location-list';

const PAGE_SIZE = 10;

export default function ItemSetQuestionsInLocationSection() {
  const { qBankStructureInformation, qBankStructureLevels, qBankStructureLevelItems } =
    useQBankStructure();
  const { itemSet } = useItemSet();
  const { questionStructures } = itemSet;

  const [currentPage, setCurrentPage] = useState(0);

  const levelItem = useMemo(() => {
    const subTopicLevel = qBankStructureLevels.find(
      (structureLevel) => structureLevel.category === QBankStructureLevelCategory.SubTopic,
    );
    const subTopicLevelItems = qBankStructureLevelItems.filter(
      (structureLevelItem) => structureLevelItem.qBankStructureLevelId === subTopicLevel?.id,
    );

    return subTopicLevelItems.find((subTopicLevelItem) =>
      questionStructures.some(
        (questionStructure) => subTopicLevelItem.id === questionStructure.refLevelItemId,
      ),
    );
  }, [qBankStructureLevels, qBankStructureLevelItems, questionStructures]);

  const {
    data: itemSetInThisLocation,
    loading: isLoadingItemSetInThisLocation,
    fetchMore: fetchMoreItemSetInThisLocation,
  } = useSearchItemSetQuestionsInLocationQuery({
    variables: {
      search: {
        levelItemId: levelItem?.id,
        refQBankStructureId: qBankStructureInformation.id,
        questionGroupType: [QuestionGroupType.ItemSet],
      },
      paging: {
        pageSize: PAGE_SIZE,
        currentPage: 0,
      },
      order: {
        field: 'createdAt',
        type: OrderType.Asc,
      },
    },
    skip: levelItem?.id === undefined,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const handleFetchMore = async () => {
    await fetchMoreItemSetInThisLocation({
      variables: {
        paging: {
          pageSize: PAGE_SIZE,
          currentPage: currentPage + 1,
        },
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult;

        const uniqueQuestions = getUniqueQuestions([
          ...previousResult.qctSearchQuestionGroups.questionGroups,
          ...fetchMoreResult.qctSearchQuestionGroups.questionGroups,
        ]);

        return {
          qctSearchQuestionGroups: {
            ...previousResult.qctSearchQuestionGroups,
            questionGroups: uniqueQuestions,
          },
        };
      },
    });
    setCurrentPage((prev) => prev + 1);
  };

  const itemSetQuestions = itemSetInThisLocation?.qctSearchQuestionGroups.questionGroups ?? [];
  const totalItemSetQuestions = itemSetInThisLocation?.qctSearchQuestionGroups.total ?? 0;

  return (
    <PaperSection title='Q in this Location'>
      <Flex
        sx={(theme) => ({
          p: theme.spacing(4, 6, 6),
          flexDirection: 'column',
          gap: theme.spacing(2),
        })}
      >
        <Flex sx={{ justifyContent: 'space-between' }}>
          <Text variant='body2Semibold'>QID</Text>
          <Text variant='body2Semibold'>{totalItemSetQuestions} Qty.</Text>
        </Flex>
        <ItemSetQuestionsInLocationList
          itemSetQuestions={itemSetQuestions}
          loading={isLoadingItemSetInThisLocation}
          hasMore={itemSetQuestions.length < totalItemSetQuestions}
          structureId={qBankStructureInformation.id}
          handleFetchMore={handleFetchMore}
          currentItemSetId={itemSet.id}
          currentPage={currentPage}
        />
      </Flex>
    </PaperSection>
  );
}
